import { Polaroid } from '@/components/Polaroid';
import styles from './stage-view.module.css';
import {
  PhotoCarouselSubmissionData as SubmissionData,
  PhotoCarouselViewConfig as ViewConfig,
  StageEngagementFragment,
  useOnMockSubmissionDeletedSubscription,
  useOnSubmissionDeletedSubscription,
  useStageGetSubmissionQuery,
} from '@/generated/graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import useWindowSize from '@/hooks/useWindowSize';
import { toFullS3Url } from '@/utils/toFullS3Url';
import annoyedStyles from '@/styles/Annoyed.module.css';
import { useMockRealtimeChannel } from '@/hooks/useMockRealtimeChannel';
import { resolveMaxVisiblePhotos } from '../consts';
import {
  appendPhotoToStack,
  moveStackedSubmissionToTop,
  removeSubmissionFromStack,
  shouldAppendFetchedSubmission,
  shouldFetchVisibleSubmission,
  shouldPromoteStackedSubmission,
} from './stagePhotos';

interface Photo {
  photoUrl: string;
  caption: string;
  rotation: number;
  scale: number;
  translateX: number;
  translateY: number;
  // can't use photoUrl because there will be duplicates
  id: number;
  submissionId: string | undefined;
}

const initialStyle = {
  opacity: 0,
  transform: 'scale(3) rotate(0deg) translate(0, 25%)',
};

const DEFAULT_NO_PHOTOS_MESSAGE = 'Looking for photos...';

function dropLayout(instanceId: number, width: number, height: number) {
  return {
    rotation: Math.round(Math.random() * 20 - 10),
    scale: 1,
    translateX: Math.round(width * 0.25 * (instanceId % 3)),
    translateY: Math.round(Math.random() * height * 0.1 - height * 0.09),
  };
}

export function PhotoCarouselStageView({
  engagement,
}: {
  engagement: StageEngagementFragment;
}) {
  const nextPhotoId = useRef(0);
  const viewConfig = engagement.viewConfig as ViewConfig;
  const maxVisiblePhotos = resolveMaxVisiblePhotos(viewConfig.maxVisiblePhotos);
  const visibleSubmissionId =
    engagement.viewData?.__typename === 'PhotoCarouselViewData'
      ? engagement.viewData.visibleSubmission
      : undefined;

  const [photos, setPhotos] = useState<Photo[]>([]);
  const suppressedSubmissionIds = useRef(new Set<string>());

  const { data /* , loading, error  */ } = useStageGetSubmissionQuery({
    skip: !shouldFetchVisibleSubmission({
      visibleSubmissionId,
      stackedSubmissionIds: photos.map((photo) => photo.submissionId),
      suppressedSubmissionIds: suppressedSubmissionIds.current,
    }),
    fetchPolicy: 'network-only',
    variables: { id: visibleSubmissionId! },
  });
  const { width, height } = useWindowSize();
  const widthRef = useRef(width);
  const heightRef = useRef(height);
  widthRef.current = width;
  heightRef.current = height;

  const mockRealtime = useMockRealtimeChannel();

  const dropSubmissionFromStage = useCallback((deletedSubmissionId: string) => {
    suppressedSubmissionIds.current.add(deletedSubmissionId);
    setPhotos((prev) => removeSubmissionFromStack(prev, deletedSubmissionId));
  }, []);

  useOnSubmissionDeletedSubscription({
    variables: { engagementId: engagement.id },
    onData: ({ data }) => {
      const deletedSubmissionId = data.data?.submissionDeleted?.submissionId;
      if (deletedSubmissionId) {
        dropSubmissionFromStage(deletedSubmissionId);
      }
    },
  });
  useOnMockSubmissionDeletedSubscription({
    skip: !mockRealtime,
    variables: { engagementId: engagement.id },
    onData: ({ data }) => {
      const deletedSubmissionId =
        data.data?.mockSubmissionDeleted?.submissionId;
      if (deletedSubmissionId) {
        dropSubmissionFromStage(deletedSubmissionId);
      }
    },
  });

  useEffect(() => {
    if (!visibleSubmissionId) {
      return;
    }
    if (suppressedSubmissionIds.current.has(visibleSubmissionId)) {
      return;
    }
    setPhotos((prev) => {
      if (
        !shouldPromoteStackedSubmission({
          visibleSubmissionId,
          stackedSubmissionIds: prev.map((photo) => photo.submissionId),
          suppressedSubmissionIds: suppressedSubmissionIds.current,
        })
      ) {
        return prev;
      }
      return moveStackedSubmissionToTop(prev, visibleSubmissionId, (photo) => {
        const id = nextPhotoId.current++;
        return {
          ...photo,
          ...dropLayout(id, widthRef.current, heightRef.current),
          id,
        };
      });
    });
    // Poll saves bump `updatedAt` even when the same photo is shown again.
  }, [visibleSubmissionId, engagement.updatedAt]);

  useEffect(() => {
    if (
      data?.submission?.data?.__typename !== 'PhotoCarouselSubmissionData' ||
      !data.submission?.id
    ) {
      return;
    }
    const submissionData = data.submission.data as SubmissionData;
    const fetchedSubmissionId = data.submission.id;
    if (submissionData.approved === false) {
      dropSubmissionFromStage(fetchedSubmissionId);
      return;
    }
    if (submissionData.approved === true) {
      suppressedSubmissionIds.current.delete(fetchedSubmissionId);
    }
    if (
      !shouldAppendFetchedSubmission({
        fetchedSubmissionId,
        visibleSubmissionId,
        suppressedSubmissionIds: suppressedSubmissionIds.current,
        stackedSubmissionIds: photos.map((photo) => photo.submissionId),
        approved: submissionData.approved,
      })
    ) {
      return;
    }
    if (!submissionData.photoUrl) {
      return;
    }
    const photoUrl = toFullS3Url(submissionData.photoUrl);
    setPhotos((prev) => {
      if (prev.some((photo) => photo.submissionId === fetchedSubmissionId)) {
        return prev;
      }
      if (prev.length > 0 && prev[prev.length - 1].photoUrl === photoUrl) {
        return prev;
      }
      const id = nextPhotoId.current++;
      return appendPhotoToStack(
        prev,
        {
          photoUrl,
          caption: submissionData.caption || '',
          ...dropLayout(id, widthRef.current, heightRef.current),
          id,
          submissionId: fetchedSubmissionId,
        },
        maxVisiblePhotos,
      );
    });
  }, [
    data,
    dropSubmissionFromStage,
    maxVisiblePhotos,
    photos,
    visibleSubmissionId,
  ]);

  return (
    <div
      data-name="PHOTOS_CONTAINER"
      className={`flex-stretch relative flex h-screen w-full items-center ${photos.length > 0 ? 'justify-start' : 'justify-center'}`}
    >
      {photos.map((photo) => (
        <Polaroid
          key={photo.id}
          className={`absolute ${styles.polaroidFadeIn} `}
          photoUrl={photo.photoUrl}
          caption={photo.caption}
          width="23vw"
          initialStyle={initialStyle}
          loadedStyle={{
            opacity: 1,
            transform: `rotate(${photo.rotation}deg) scale(${photo.scale}) translate(${photo.translateX}px, ${photo.translateY}px)`,
          }}
        />
      ))}
      {photos.length === 0 && (
        <div
          data-name="PHOTOS-EMPTY"
          className={`font-screen-body text-[3vw] ${annoyedStyles.annoyedShake} text-screen-foreground`}
        >
          {viewConfig.noPhotosMessage || DEFAULT_NO_PHOTOS_MESSAGE}
        </div>
      )}
    </div>
  );
}
